import { useCallback, useEffect, useId } from 'react';
import { useNavigate, type NavigateFunction, type NavigateOptions } from 'react-router-dom';
import { confirmDialog } from '@/components/ConfirmDialog';
import {
  hasUnsavedChanges,
  isPageUnloadApproved,
  leavesPage,
  setUnsavedChangesBlocker,
  setUnsavedChangesMessage,
  unsavedGuardEntry,
  unsavedLeaveMessage,
} from '@/utils/unsavedChanges';

const DEFAULT_MESSAGE = 'You have unsaved changes. Leave without saving?';

/**
 * One guard for the whole page, however many forms on it are dirty: two unsaved cards must not
 * mean two prompts or two spare history entries. Each hook instance registers a `leave` entry in
 * the unsaved-changes registry; the listeners are installed when the first registers and removed
 * when the last leaves.
 */
let uninstall: (() => void) | null = null;
// Set by UnsavedChangesNavigator, for a link that was removed while the question was open.
let routerNavigate: NavigateFunction | null = null;

function askToLeave(message = unsavedLeaveMessage() ?? DEFAULT_MESSAGE): Promise<boolean> {
  return confirmDialog({ title: 'Leave this page?', description: message, confirmLabel: 'Leave' });
}

/** The router's index for the current history entry, or null outside the router. */
function historyIdx(): number | null {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
}

function install(): () => void {
  // Set once the person answers Leave to Back; until then, Back and Forward ask.
  let leaving = false;
  // Set once this install is removed (the work was saved), for a question still open then.
  let removed = false;
  // Whether removing it dropped the spare entry, leaving this tab on the guarded entry.
  let droppedSpare = false;
  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    // The person already agreed to lose the work (signing out here or elsewhere), or
    // answered Leave to Back and the entry behind is another site: don't ask a second time.
    if (isPageUnloadApproved() || leaving) return;
    event.preventDefault();
    event.returnValue = '';
  };

  // A modal cannot answer inside the click event, so the click is stopped first and, once the user
  // agrees to leave, replayed with the guard standing aside.
  let replaying = false;
  const onClick = (event: MouseEvent) => {
    if (replaying) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
    // A link that opens something over this page, which keeps its unsaved work (a task dialog's
    // `?task=`), says so with data-keeps-page.
    if (anchor.hasAttribute('data-keeps-page')) return;
    const url = new URL(anchor.href, window.location.href);
    // Other sites go through beforeunload; links within the same page do not leave it.
    if (!leavesPage(url, window.location)) return;
    // The router's Link skips navigation when the click was already prevented.
    event.preventDefault();
    event.stopPropagation();
    void askToLeave().then((leave) => {
      if (!leave) return;
      // The question can close the menu the link was in; go there through the router instead.
      if (!anchor.isConnected) {
        routerNavigate?.(url.pathname + url.search + url.hash);
        return;
      }
      replaying = true;
      try {
        anchor.click();
      } finally {
        replaying = false;
      }
    });
  };

  // Back cannot be blocked with BrowserRouter, so a spare history entry for this same page absorbs
  // it: going back lands on the guarded entry below, the page stays mounted, and only then is the
  // question asked.
  //
  // The entries are known by the router's `idx`, not their address: the page may replace its own
  // URL while guarded (a list filter beside a record's editor, or a remembered view put back in the
  // URL as Back lands, before this listener runs). A replace keeps `idx` and a push adds one; the
  // spare is a copy of the guarded entry's state, so both have the same `idx`. Without an `idx`
  // (no router), the address is all there is.
  const guardIdx = historyIdx();
  /** The guarded entry's address when the spare was put on it. */
  let belowHref = window.location.href;
  let offSpare = false;
  const atGuardIdx = () => (guardIdx === null ? window.location.href === belowHref : historyIdx() === guardIdx);
  /** Whether the top entry is still the spare (Back or Forward off it comes through popstate). */
  const onSpare = () => !offSpare && atGuardIdx();
  const addSpareEntry = () => {
    belowHref = window.location.href;
    window.history.pushState(window.history.state, '', belowHref);
    offSpare = false;
  };
  addSpareEntry();
  const onPopState = () => {
    if (leaving) return;
    if (!atGuardIdx()) {
      offSpare = true;
      return;
    }
    // Back has landed on the guarded entry, so the page is still here while the question is open.
    // The spare is put back straight away: a second Back during the question is absorbed too.
    addSpareEntry();
    void askToLeave().then((leave) => {
      if (!leave) return;
      // Saved while the question was open: the spare is gone already, so one step back leaves.
      if (removed) {
        if (droppedSpare && atGuardIdx()) window.history.back();
        return;
      }
      // Anywhere but the spare, the person has moved on already.
      if (!onSpare()) return;
      leaving = true;
      window.history.go(-2);
      // A tab opened on this page has nothing behind it, so go(-2) goes nowhere: stay, and keep
      // asking. A real move changes the entry (or unloads the page) well before this runs.
      window.setTimeout(() => {
        if (!removed && onSpare()) leaving = false;
      }, 1000);
    });
  };

  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('click', onClick, true);
  window.addEventListener('popstate', onPopState);
  return () => {
    removed = true;
    window.removeEventListener('beforeunload', onBeforeUnload);
    document.removeEventListener('click', onClick, true);
    window.removeEventListener('popstate', onPopState);
    // Saved while still on the page: drop the spare entry so Back works normally again.
    // After a real navigation the address has changed, and going back would undo it. A full-page
    // navigation is already under way once unload is approved, and going back would race it.
    if (leaving || isPageUnloadApproved() || !onSpare()) return;
    droppedSpare = true;
    if (window.location.href === belowHref) {
      window.history.back();
      return;
    }
    // The page replaced its URL meanwhile: drop the spare all the same, then put that URL on the
    // entry below, so neither the change nor a stray entry is left behind.
    const kept = new URL(window.location.href);
    const keptState = (window.history.state as { usr?: unknown } | null)?.usr;
    window.addEventListener(
      'popstate',
      () => {
        const to = kept.pathname + kept.search + kept.hash;
        if (routerNavigate) routerNavigate(to, { replace: true, state: keptState });
        else window.history.replaceState(window.history.state, '', to);
      },
      { once: true }
    );
    window.history.back();
  };
}

/**
 * Ask before unsaved work is lost, however the page is left: reload or tab close (the browser's own
 * wording), any in-app link including the sidebar, the top bar's menus and the bell's rows, the
 * browser's Back button, Cmd+K (through `useGuardedNavigate`), and signing out.
 *
 * `saving`: a save of this work is in flight. Leaving stops asking, so moving on straight after a
 * save doesn't, but signing out still asks until the save settles (a failed save keeps the work).
 *
 * Returns `confirmLeave` for navigation done in code (`navigate()` after a button press), which
 * link interception cannot see. It resolves to true when it is fine to go.
 */
export function useUnsavedChangesGuard(
  enabled: boolean,
  message: string = DEFAULT_MESSAGE,
  { saving = false }: { saving?: boolean } = {}
) {
  const key = `guard:${useId()}`;

  // The wording is not a dependency here: reinstalling would go back and add a spare entry again
  // on every change, which races the browser's own history.
  useEffect(() => {
    const entry = unsavedGuardEntry(enabled, saving);
    if (!entry) return;
    setUnsavedChangesBlocker(key, entry);
    if (entry.leave && !uninstall) uninstall = install();
    return () => {
      setUnsavedChangesBlocker(key, false);
      if (!hasUnsavedChanges('leave') && uninstall) {
        uninstall();
        uninstall = null;
      }
    };
  }, [enabled, saving, key]);

  // After the entry above (effects run in order), so a re-registered entry gets its wording back.
  useEffect(() => {
    if (enabled) setUnsavedChangesMessage(key, message);
  }, [enabled, saving, key, message]);

  const confirmLeave = useCallback(
    async () => !enabled || saving || (await askToLeave(message)),
    [enabled, saving, message]
  );
  return { confirmLeave };
}

/** Gives the guard the router, for links that are gone by the time Leave is pressed. Mount once, inside the router. */
export function UnsavedChangesNavigator(): null {
  const navigate = useNavigate();
  useEffect(() => {
    routerNavigate = navigate;
    return () => {
      if (routerNavigate === navigate) routerNavigate = null;
    };
  }, [navigate]);
  return null;
}

/**
 * `navigate()` that asks first when a guard is on and `to` leaves the page, for places that move
 * the page in code rather than through a link (Cmd+K). Resolves to whether it navigated.
 */
export function useGuardedNavigate(): (to: string, options?: NavigateOptions) => Promise<boolean> {
  const navigate = useNavigate();
  return useCallback(
    async (to: string, options?: NavigateOptions) => {
      if (
        hasUnsavedChanges('leave') &&
        leavesPage(new URL(to, window.location.href), window.location) &&
        !(await askToLeave())
      ) {
        return false;
      }
      navigate(to, options);
      return true;
    },
    [navigate]
  );
}
